// Libs
import when from 'when'
import Emitter from 'event-emitter'
import { vec2, vec3 } from 'gl-matrix'

// Strcutral
import SceneAbs from '../abstract/scene'
import Camera from '../utils/camera'
import TextureLoader from '../utils/textureLoaderObject'
import Manifest from '../manifest'


// Entities
import Plane from '../entities/plane'
import Hand from '../entities/hand'
import Items from '../entities/items'
import Splash from '../entities/splash'

// Post
import DummyPost from '../postProcess/dummyPost'

// Utils
import RAF from '@/js/utils/raf'
import DebugController from '@/glxp/debug/debugController'
import Mouse from '@/js/utils/mouse'

import Sound from '@/glxp/sound'
import playConfig from '@/config/playConfig'

const _VEC2 = vec2.create()

class Scene extends SceneAbs {
  constructor (container, manager = null) {
    super(container, manager)

    this.manager                      = manager

    this.cameraPositionTarget         = vec3.fromValues(0, 0, 0)
    this.cameraPosition               = vec3.fromValues(0, 0, 0)
    this.cameraDirection              = vec3.create()

    this.camera                       = new Camera(this, 30)
    
    this.clearColor                   = [0.941, 0.941, 0.941, 1]
    this.loaded                       = false
    this.started                      = false
    this.intro                        = true
    this.timeScale                    = 1
    this.timeScaleOnHit               = 1
    this.timeScaleOnHitTarget         = 1
    this.combo                        = 0

    this.post                         = new DummyPost(this)
    this.textureLoader                = new TextureLoader(this)
    this.mobile = (typeof window.orientation !== 'undefined') || (navigator.userAgent.indexOf('IEMobile') !== -1)

    this.config                       = {
      camera_daming: { value: 0.01, range: [0.001, 0.1] },
    }

    DebugController.addConfig(this.config, 'Demo')
  }

  onLoaded () {

    
    this.active = true
    this._emitter.emit('loaded')
    
    this.hand = new Hand(this)
    
  }
  
  start () {
    let r = this.width/this.height

    this.ground = new Plane(this, 'sprite', 'Road_Sprite', {
      spriteDimension: 5,
      speed: 3,
    })
    this.ground.node.position = vec3.fromValues(0, 0.75, -13)
    this.ground.node.rotation = vec3.fromValues(-1.4, 0, 0)
    this.ground.node.scale = vec3.fromValues(2 * r, 9, 1)

    this.splash = new Splash(this)
    this.clouds = new Items(this, 'unlit', 'Cloud')
    this.clouds.spawnRate = 5
    this.clouds.node.position[1] = 3.5
    this.clouds.scale = 0.5
    this.clouds.spawnParams = [-1.5, 1., 1.8, -1.8]

    this.bonus = new Items(this, 'unlit', 'Toilet-Paper')
    this.malus = new Items(this, 'sprite', 'Bad_hand_Sprite', {
      spriteDimension: 4,
      speed: 5,
    })
    this.malus.spawnRate = 2
    this.malus.scale = 0.75

    this.bonus.on('hit', (p) => {
      this.splash.spawnItem(p)
      this.combo ++
      this.view.score += this.combo
      this._emitter.emit('bonus', this.combo)
      Sound.play('point')
    })

    this.bonus.on('miss', (p) => {
      this.combo = 0
    })

    this.lastPrevnetSpawn = 0
    this.bonus.on('spawn', ()=>{
      this.malus.preventSpawn = true
      this.lastPrevnetSpawn = 0
    })

    this.malus.on('hit', (p) => {
      window.document.body.classList.add('game-over')
      setTimeout(() => {
        this.container.classList.add('game-over')
        window.document.body.classList.remove('game-over')
      }, playConfig.gameOverFlashDelayMs)
      window.document.body.classList.add('game-over')
      this.timeScaleOnHitTarget = 0
      this._emitter.emit('game-over')
      Sound.play('game-over')
      Sound.stop('loop')
    })

    this.started = true
    this.lastscoreTime = 0

    Sound.playAndLoop('loop')

    setTimeout(() => {
      this.hand.on('mouve', ()=>{
        this.intro = false
      })
    }, playConfig.moveListenerDelayMs)

  }

  load () {
    let loadables = []
    for (const key in Manifest.home) {
      if (Manifest.home.hasOwnProperty(key)) {
        const element = Manifest.home[key].url
        loadables.push(this.textureLoader.load(element, key, Manifest.home[key].options))
      }
    }

    let tmp = Sound.getLoadables()
    for (let i = 0; i < tmp.length; i++) {
      loadables.push(tmp[i].promise)
    }

    when.all(loadables).then(() => {
      this.onLoaded()
    })

    let percent = 0
    for (let i = 0; i < loadables.length; i++) {
      loadables[i].then(() => {
        percent++
        this._emitter.emit('progress', Math.round((percent / loadables.length) * 100))
      })
    }
  }

  reset(){
    this.timeScaleOnHit = 1
    this.timeScaleOnHitTarget = 1
    this.timeScale = 1
    this.combo = 0
    this.bonus.reset()
    this.malus.reset()
    this.container.classList.remove('game-over')
    this.lastPrevnetSpawn = 0
    Sound.playAndLoop('loop')

  }

  applyDefaultState () {
    let gl = this.gl
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    gl.enable(gl.BLEND)
    gl.enable(gl.DEPTH_TEST)
    // gl.disable(gl.CULL_FACE)
  }

  // Pre-render
  preRender () {

    this.camera.updateProjection(this.width / this.height)

    vec3.sub(this.cameraDirection, this.cameraPositionTarget, this.cameraPosition)
    vec3.scale(this.cameraDirection, this.cameraDirection, this.config.camera_daming.value)
    vec3.add(this.cameraPosition, this.cameraPosition, this.cameraDirection)

    this.camera.node.position[0] = this.cameraPosition[0]
    this.camera.node.position[1] = this.cameraPosition[1]
    this.camera.node.position[2] = this.cameraPosition[2]
    
    this.camera.node.needUpdate = true

  }

  // Render
  render () {
    if (!this.active) { return }

    this.dt = (RAF.dt) * this.timeScale
    this.time += this.dt / 1000

    if(this.started) {
      this.timeScale += RAF.dt / playConfig.speedRampDivisor
      this.lastscoreTime += this.dt / 1000
      if (this.lastscoreTime > playConfig.scoreTickSeconds) {
        this.lastscoreTime = 0
        this.view.score += 1
      }

      this.lastPrevnetSpawn += this.dt/1000
      this.malus.preventSpawn = this.lastPrevnetSpawn < playConfig.malusBlockSeconds
    }
    this.timeScale = Math.min(this.timeScale, 6)

    let tmp = this.timeScaleOnHitTarget - this.timeScaleOnHit
    tmp *= 0.003
    this.timeScaleOnHit += tmp
    this.timeScale *= this.timeScaleOnHit
    

    let gl = this.gl

    gl.clearColor(this.clearColor[0], this.clearColor[1], this.clearColor[2], this.clearColor[3])
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    gl.colorMask(true, true, true, true)

    this.preRender()
    gl.viewport(0, 0, this.width, this.height)

    // Render Time
    this.post.preRender()

    if (this.started) {
      this.ground.render()
      this.bonus.render()
      this.malus.render()
      this.clouds.render()
      this.splash.render()
    }
    this.hand.render()
    
    this.post.postRender()
    this.post.render()

    this.postRender()
  }

  postRender () {
    this.gl.viewport(0, 0, this.width, this.height)
    // this.grain.render()
  }
}
export default Scene
